import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, AlertTriangle, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { viagensService } from '../../services/viagens';
import { formatDate } from '../../utils/formatters';

/**
 * O checklist de saída, do lado de quem decide.
 *
 * O motorista responde 25 itens; o gestor não vai ler 25 linhas verdes. Então a
 * tela abre pelo que está errado — item reprovado, com o que ele escreveu e a
 * foto — e guarda a lista completa atrás de um clique, para quando alguém
 * precisar conferir que tudo foi olhado.
 */
const ChecklistDaViagem: React.FC<{ viagemId: number }> = ({ viagemId }) => {
  const [mostrandoTudo, setMostrandoTudo] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['viagem', viagemId, 'checklist'],
    queryFn: () => viagensService.getChecklist(viagemId),
    // 404 aqui é informação, não falha: a viagem ainda não foi conferida.
    retry: false,
  });

  if (isLoading) return null;

  const checklist = data?.data;

  if (error || !checklist) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-gray-400" /> Checklist de saída
        </h2>
        <p className="text-sm text-gray-500">
          Esta viagem ainda não foi conferida. O motorista preenche o checklist no portal dele, e sem ele a
          viagem não inicia.
        </p>
      </div>
    );
  }

  const reprovados = checklist.reprovados ?? [];

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-gray-400" /> Checklist de saída
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {checklist.motorista ? `Conferido por ${checklist.motorista}` : 'Conferido pelo motorista'}
            {checklist.created_at ? ` em ${formatDate(checklist.created_at)}` : ''}
          </p>
        </div>

        <span
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
            reprovados.length > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {reprovados.length > 0 ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5" /> {reprovados.length} item(ns) com problema
            </>
          ) : (
            <>
              <Check className="w-3.5 h-3.5" /> Tudo conforme
            </>
          )}
        </span>
      </div>

      {/* O que está errado vem primeiro, e sem precisar de clique: é o que muda
          a decisão de deixar o caminhão sair. */}
      {reprovados.length > 0 && (
        <ul className="space-y-2 mb-4">
          {reprovados.map((item) => (
            <li key={item.chave} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-sm font-medium text-red-900">{item.rotulo}</p>
              {item.observacao && <p className="text-sm text-red-800 mt-0.5">{item.observacao}</p>}
            </li>
          ))}
        </ul>
      )}

      {checklist.observacoes && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 mb-4">
          <p className="text-xs font-medium text-gray-500">Observações do motorista</p>
          <p className="text-sm text-gray-800 mt-0.5">{checklist.observacoes}</p>
        </div>
      )}

      {checklist.fotos.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-500 mb-2">Fotos do veículo</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {checklist.fotos.map((foto) => (
              // Abre em aba nova em vez de lightbox: o gestor quer dar zoom, e
              // o visualizador do navegador faz isso melhor do que qualquer
              // modal que eu escrevesse.
              <a
                key={foto.caminho}
                href={foto.url}
                target="_blank"
                rel="noreferrer"
                className="block aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400"
              >
                <img src={foto.url} alt="Foto do checklist" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setMostrandoTudo((v) => !v)}
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
      >
        {mostrandoTudo ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {mostrandoTudo ? 'Ocultar a lista completa' : `Ver os ${checklist.itens.length} itens conferidos`}
      </button>

      {mostrandoTudo && (
        <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
          {checklist.itens.map((item) => (
            <li key={item.chave} className="py-2 flex items-start justify-between gap-3">
              <span className="text-sm text-gray-700">
                {item.rotulo}
                {item.observacao && <span className="block text-xs text-gray-500">{item.observacao}</span>}
              </span>
              <span
                className={`flex-shrink-0 text-xs font-semibold ${
                  item.ok ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {item.ok ? 'OK' : 'Não'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ChecklistDaViagem;
